import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from './cn'
import { Spinner } from './spinner'
import { Tooltip } from './tooltip'

export const buttonVariants = cva(
  'relative inline-flex shrink-0 select-none items-center justify-center gap-2 whitespace-nowrap rounded-control font-medium ' +
  'transition-[background-color,border-color,color,box-shadow,transform] duration-150 ease-out ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus ' +
  'disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 ' +
  'active:translate-y-px [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground hover:bg-primary-hover [box-shadow:inset_0_1px_0_rgb(255_255_255/0.12),var(--shadow-control-v)]',
        secondary: 'border border-border-strong bg-surface text-text shadow-control hover:bg-surface-hover hover:border-input',
        outline: 'border border-border-strong bg-transparent text-text hover:bg-surface-hover',
        ghost: 'bg-transparent text-text-muted hover:bg-surface-active hover:text-text',
        danger: 'bg-danger text-white hover:bg-danger-hover [box-shadow:inset_0_1px_0_rgb(255_255_255/0.12),var(--shadow-control-v)]',
        'danger-ghost': 'bg-transparent text-danger-strong hover:bg-danger-soft',
        link: 'h-auto! px-0! bg-transparent text-primary-strong underline-offset-4 hover:underline active:translate-y-0',
      },
      size: {
        sm: 'h-7 px-3 text-sm [&_svg]:size-3.5',
        md: 'h-8 px-3 text-base [&_svg]:size-4',
        lg: 'h-10 px-4 text-base [&_svg]:size-4',
      },
      iconOnly: { true: 'px-0', false: '' },
    },
    compoundVariants: [
      { iconOnly: true, size: 'sm', class: 'w-7' },
      { iconOnly: true, size: 'md', class: 'w-8' },
      { iconOnly: true, size: 'lg', class: 'w-10' },
    ],
    defaultVariants: { variant: 'secondary', size: 'md', iconOnly: false },
  },
)

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, Omit<VariantProps<typeof buttonVariants>, 'iconOnly'> {
  /** Shows a spinner, disables the button and keeps its width. */
  loading?: boolean
  /** Icon before the label (lucide element). */
  icon?: ReactNode
  /** Icon after the label. */
  iconRight?: ReactNode
  /** Render the child element instead (e.g. a router <Link>) with button styling. */
  asChild?: boolean
  /** Tooltip text (required for icon-only buttons; also used as aria-label). */
  tooltip?: ReactNode
  /** Keyboard hint shown in the tooltip, e.g. "mod+s". */
  shortcut?: string
  fullWidth?: boolean
}

/**
 * Button. `<Button variant="primary" loading={m.isPending} onClick={save}>Save</Button>`
 * Icon-only: `<Button icon={<Trash2/>} tooltip="Delete" variant="ghost"/>`.
 * As a link: `<Button asChild><Link to="/products/new">Add product</Link></Button>`.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, loading, icon, iconRight, asChild, tooltip, shortcut, fullWidth, children, disabled, type, ...props }, ref,
) {
  const iconOnly = !children && !!icon && !asChild
  const Comp = asChild ? Slot : 'button'
  const label = typeof tooltip === 'string' ? tooltip : undefined
  const btn = (
    <Comp
      ref={ref}
      type={asChild ? undefined : (type ?? 'button')}
      className={cn(buttonVariants({ variant, size, iconOnly }), fullWidth && 'w-full', className)}
      disabled={asChild ? undefined : disabled || loading}
      aria-disabled={asChild && (disabled || loading) ? true : undefined}
      aria-busy={loading || undefined}
      aria-label={iconOnly ? (props['aria-label'] ?? label) : props['aria-label']}
      {...props}
    >
      {asChild ? children : (
        <>
          {loading && <span className="absolute inset-0 grid place-items-center"><Spinner size={size === 'sm' ? 14 : 16} /></span>}
          <span className={cn('inline-flex items-center gap-2', loading && 'invisible')}>
            {icon}
            {children}
            {iconRight}
          </span>
        </>
      )}
    </Comp>
  )
  if (!tooltip) return btn
  return <Tooltip content={tooltip} shortcut={shortcut}>{btn}</Tooltip>
})

export interface IconButtonProps extends Omit<ButtonProps, 'icon' | 'children' | 'tooltip'> {
  /** The icon element. */
  icon: ReactNode
  /** Accessible name + tooltip (required). */
  label: string
  /** Hide the tooltip (still labelled for screen readers). */
  noTooltip?: boolean
}

/** Square icon button with a required label: `<IconButton icon={<X/>} label="Close" />`. Defaults to ghost. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, noTooltip, variant = 'ghost', ...props }, ref,
) {
  return <Button ref={ref} variant={variant} icon={icon} aria-label={label} tooltip={noTooltip ? undefined : label} {...props} />
})
